import { useEffect, useState } from "react";
import { useSession } from "./lib/useSession";
import { pideCopiaDelMostrador, useSyncPos } from "./lib/useSyncPos";
import { useRuta, RUTAS_INVENTARIO, type RutaId } from "./lib/ruta";
import { setBoticaMostrador, useBoticaTrabajo } from "./lib/botica-trabajo";
import { BarraBotica, ElegirBotica } from "./components/BoticaTrabajo";
import { Grabadora } from "./pages/Grabadora";
import { LoginPage } from "./pages/LoginPage";
import { Layout } from "./components/Layout";
import { Mostrador } from "./pages/Mostrador";
import { Recepcion } from "./pages/Recepcion";
import { Clientes } from "./pages/Clientes";
import { Inventario } from "./pages/Inventario";
import { Caja } from "./pages/Caja";
import { Dashboard } from "./pages/Dashboard";
import { Usuarios } from "./pages/admin/Usuarios";
import { Sucursales } from "./pages/admin/Sucursales";
import { CatalogoForm } from "./pages/admin/CatalogoForm";
import { CatalogoImportador } from "./pages/admin/CatalogoImportador";
import { CatalogoPrueba } from "./pages/admin/CatalogoPrueba";
import { Sugerencias } from "./pages/admin/Sugerencias";
import { Copiloto } from "./pages/admin/Copiloto";
import { Reconocimiento } from "./pages/admin/Reconocimiento";
import { Reposiciones } from "./pages/admin/Reposiciones";
import { Proveedores } from "./pages/admin/Proveedores";
import { Pedido } from "./pages/admin/Pedido";
import { Grabadores } from "./pages/admin/Grabadores";
import { AudioCalidad } from "./pages/admin/AudioCalidad";
import { Casos } from "./pages/admin/Casos";
import { Consolidado } from "./pages/admin/Consolidado";
import { Hoy } from "./pages/Hoy";
import { Ajustes } from "./pages/admin/Ajustes";
import { Mapa } from "./pages/Mapa";
import { VentaTicket } from "./pages/VentaTicket";
import { MiCuenta } from "./pages/MiCuenta";
import { ExamenDelDia } from "./pages/Examen";
import { Aprendizaje } from "./pages/admin/Aprendizaje";
import { AvisoVersion } from "./components/AvisoVersion";
import type { SesionActiva, SucursalMin } from "./lib/tipos";

// La grabadora del A10 es una página de DISPOSITIVO (token propio, sin sesión de usuario). Se resuelve
// ANTES que el flujo de login para que funcione sin cuenta (B10.1 §8).
function useEsGrabadora(): boolean {
  const [hash, setHash] = useState(() => (typeof window === "undefined" ? "" : window.location.hash));
  useEffect(() => {
    const onHash = () => setHash(window.location.hash);
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);
  return hash.startsWith("#/grabadora");
}

// QR del ticket (Kevin, 12-oct-2026): `#/venta/<client_uuid>`. Lleva un dato en la dirección, así que
// no entra en VISTAS (que compara el hash exacto). Si no hay sesión el login se pinta primero y el
// hash sigue ahí, así que al entrar la persona cae directo en la venta que escaneó.
const RE_VENTA = /^#\/venta\/([0-9a-f-]{36})$/i;
function useVentaDelHash(): string | null {
  const [hash, setHash] = useState(() => (typeof window === "undefined" ? "" : window.location.hash));
  useEffect(() => {
    const onHash = () => setHash(window.location.hash);
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);
  return RE_VENTA.exec(hash)?.[1] ?? null;
}

export function App() {
  const esGrabadora = useEsGrabadora();
  return (
    <>
      <AvisoVersion />
      {esGrabadora ? <Grabadora /> : <AppUsuario />}
    </>
  );
}

function AppUsuario() {
  const { estado, entrar, salir } = useSession();
  const rol = estado.status === "authenticated" ? estado.sesion.usuario.rol : null;
  const esDueno = rol === "super_admin";
  const [botica, elegirBotica] = useBoticaTrabajo(estado.status === "authenticated" ? estado.sesion.usuario.id : null, esDueno);
  // De quién es la copia del mostrador (tarea 2.6): otra cuenta u otra botica en este equipo la renueva.
  // Para el dueño, la botica es la que eligió: cambiarla tira los precios y el stock de la anterior.
  const sucCopia = esDueno ? (botica?.id ?? "") : estado.status === "authenticated" ? (estado.sesion.usuario.sucursalId ?? "") : "";
  const dueno = estado.status === "authenticated" ? `${estado.sesion.usuario.id}|${sucCopia}` : null;
  const conCopia = rol !== null && pideCopiaDelMostrador(rol, botica?.id ?? null);
  useSyncPos(estado.status === "authenticated", conCopia, dueno, esDueno ? (botica?.id ?? null) : null);

  if (estado.status === "loading") {
    return (
      <main className="min-h-screen flex items-center justify-center">
        <p className="opacity-60">Cargando sesión...</p>
      </main>
    );
  }
  if (estado.status === "anonymous") {
    return <LoginPage onEntrar={entrar} />;
  }
  return <AppAutenticada sesion={estado.sesion} botica={botica} onElegirBotica={elegirBotica} onSalir={() => void salir()} />;
}

// Las pantallas donde el dueño ATIENDE en una botica (Kevin, 8-oct-2026). Las demás del shell del
// mostrador (Avisos, y en el celular Hoy, Casos o el Inventario) siguen con su propio selector.
const ATIENDE_EN_BOTICA: RutaId[] = ["mostrador", "recepcion"];

function AppAutenticada({
  sesion,
  botica,
  onElegirBotica,
  onSalir,
}: {
  sesion: SesionActiva;
  botica: SucursalMin | null;
  onElegirBotica: (b: SucursalMin | null) => void;
  onSalir: () => void;
}) {
  const ruta = useRuta(sesion.usuario.rol);
  const esDueno = sesion.usuario.rol === "super_admin";
  const atiende = esDueno && ATIENDE_EN_BOTICA.includes(ruta);
  // Al pintar, no en un efecto (botica-trabajo.ts): las lecturas del Mostrador ya salen con su botica.
  // Al Encargado y al vendedor el servidor no les lee el header (su botica es la de su cuenta), pero
  // cada venta en cola guarda igual su botica: si la cuenta pasa a Dueño con ventas sin enviar, llegan
  // a la botica donde se cobraron en vez de rebotar por «elige botica».
  const enMostrador = ATIENDE_EN_BOTICA.includes(ruta);
  setBoticaMostrador(!enMostrador ? null : esDueno ? (botica?.id ?? null) : sesion.usuario.sucursalId);
  // En el Mostrador el dueño es «Dueño · Huayruro VES»: el ticket, la cabecera y lo que depende de una
  // botica (clientes, seguimiento, sugerencias) leen esta sesión. En el panel sigue siendo de la cadena.
  const sesionVista: SesionActiva = atiende && botica ? { ...sesion, sucursal: botica } : sesion;
  const ventaUuid = useVentaDelHash();

  if (ventaUuid) {
    return (
      <Layout sesion={sesion} rutaActual={ruta} onSalir={onSalir}>
        <VentaTicket key={ventaUuid} clientUuid={ventaUuid} sesion={sesion} />
      </Layout>
    );
  }
  if (atiende && !botica) {
    return (
      <Layout sesion={sesion} rutaActual={ruta} onSalir={onSalir}>
        <ElegirBotica onElegir={onElegirBotica} />
      </Layout>
    );
  }
  return (
    <Layout sesion={sesionVista} rutaActual={ruta} onSalir={onSalir}>
      {/* key por pantalla: una confirmación de «cambiar» abierta no se arrastra al pasar a Recepción. */}
      {/* En el Mostrador la barra va DENTRO de su fila de acciones (sin scroll, Kevin 15-oct-2026). */}
      {atiende && botica && ruta !== "mostrador" && <BarraBotica key={ruta} botica={botica} onCambiar={() => onElegirBotica(null)} />}
      {ruta === "hoy" && <Hoy sesion={sesion} />}
      {/* key: cambiar de botica arma un Mostrador nuevo (carrito vacío): una venta no mezcla dos boticas. */}
      {ruta === "mostrador" && (
        <Mostrador
          key={sesionVista.sucursal?.id ?? "propia"}
          sesion={sesionVista}
          barraBotica={
            atiende && botica
              ? (enFila: boolean) => <BarraBotica botica={botica} onCambiar={() => onElegirBotica(null)} enFila={enFila} />
              : undefined
          }
        />
      )}
      {ruta === "recepcion" && <Recepcion key={sesionVista.sucursal?.id ?? "propia"} sesion={sesionVista} />}
      {ruta === "clientes" && <Clientes sesion={sesion} />}
      {ruta === "reconocimiento" && <Reconocimiento sesion={sesion} />}
      {/* Inventario y sus pestañas con dirección propia (Por vencer, Stock bajo, Faltantes, Conteo,
          Por aprobar): UN solo elemento en UNA sola posición, así cambiar de pestaña no desmonta la
          pantalla ni pierde la botica elegida por el dueño. */}
      {RUTAS_INVENTARIO.includes(ruta) && <Inventario sesion={sesion} ruta={ruta} />}
      {ruta === "caja" && <Caja sesion={sesion} />}
      {ruta === "dashboard" && <Dashboard sesion={sesion} />}
      {ruta === "usuarios" && <Usuarios sesion={sesion} />}
      {ruta === "sucursales" && <Sucursales sesion={sesion} />}
      {ruta === "catalogo" && <CatalogoForm sesion={sesion} />}
      {ruta === "importar-catalogo" && <CatalogoImportador sesion={sesion} />}
      {ruta === "catalogo-prueba" && <CatalogoPrueba sesion={sesion} />}
      {ruta === "proveedores" && <Proveedores sesion={sesion} />}
      {ruta === "pedido" && <Pedido sesion={sesion} />}
      {ruta === "sugerencias" && <Sugerencias sesion={sesion} />}
      {ruta === "copiloto" && <Copiloto />}
      {ruta === "reposiciones" && <Reposiciones sesion={sesion} />}
      {ruta === "grabadores" && <Grabadores sesion={sesion} />}
      {ruta === "audio-calidad" && <AudioCalidad sesion={sesion} />}
      {ruta === "casos" && <Casos sesion={sesion} />}
      {ruta === "consolidado" && <Consolidado sesion={sesion} />}
      {ruta === "ajustes" && <Ajustes sesion={sesion} />}
      {ruta === "mapa" && <Mapa sesion={sesion} />}
      {ruta === "mi-cuenta" && <MiCuenta sesion={sesionVista} onSalir={onSalir} />}
      {ruta === "examen" && <ExamenDelDia />}
      {ruta === "aprendizaje" && <Aprendizaje sesion={sesion} />}
    </Layout>
  );
}
